import { Store } from '@ngrx/store';
import { Injectable, OnDestroy } from '@angular/core';

import { Observable } from 'rxjs/Rx';

// Constants
import { ROLES_AND_PERMISSIONS } from '../../shared/constants/roles-and-permissions';

// Reducers
import * as fromProject from '../../project/reducers';

@Injectable()
export class RolesAndPermissionsService implements OnDestroy {

  // Constructor
  constructor(
    private store: Store<fromProject.State>
  ) {}

  // On service destroy
  ngOnDestroy() {}

  private getUserRolesAndPermissions(): any[] {
    let roles;

    this.store.select(state => state.project.currentUserRolesAndPermissions)
      .do(data => { roles = data; })
      .first().subscribe();

    return roles;
  }

  private getProjectPermissions(): any[] {
    let permissions;

    this.store.select(state => state.project.userProjectPermissions)
      .do(data => { permissions = data; })
      .first().subscribe();

    return permissions;
  }

  // Private method to find user role
  private hasUserRole(roles: any[] = [], i: number): boolean {
    if (!roles || roles.length === 0) {
      roles = this.getUserRolesAndPermissions();
      if (!roles || roles.length === 0) return false;
      else return roles[i].userRole;
    } else return roles[i].userRole;
  }

  // Public method to find user permission
  public hasPermission(roles: any[] = [], roleId: number, permission: string): boolean {
    if (!roles || roles.length === 0) {
      roles = this.getUserRolesAndPermissions();
      if (!roles || roles.length === 0) return false;
      else {
        const index = roles[roleId].permissions.findIndex(item => item.id === permission);
        return roles[roleId].permissions[index].hasPermission;
      }
    } else {
      const index = roles[roleId].permissions.findIndex(item => item.id === permission);
      return roles[roleId].permissions[index].hasPermission;
    }
  }

  // Public method to find user permission
  public hasSpecialPermission(permissions: any[] = [], permission: string): boolean {
    if (!permissions || permissions.length === 0) {
      permissions = this.getProjectPermissions();
      if (!permissions || permissions.length === 0) return false;
      else {
        const index = permissions.findIndex(item => item === permission);
        return index >= 0;
      }
    } else {
      const index = permissions.findIndex(item => item === permission);
      return index >= 0;
    }
  }

  private getProjectStatus(): number {
    let status;

    this.store.select(state => state.project.project)
      .do(data => { status = data ? data.statusId : null; })
      .first().subscribe();

    return status;
  }

  public includesUserRole(roles: any[], rolesId: number[]): boolean {
    if (!roles || roles.length === 0) {
      roles = this.getUserRolesAndPermissions();

      if (!roles || roles.length === 0)
        return false;
      else {
        let activate = false;
        rolesId.forEach(role => {
          activate = activate || roles[role].userRole;
        });
      }
    } else {
      let activate = false;
      rolesId.forEach(role => {
        activate = activate || roles[role].userRole;
      });

      return activate;
    }
  }

  // ===================== GENERAL ROLES =====================
  // =========================================================

  // Is Project Owner
  public isOwner(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 0);
  }
  // Is Project Administrator
  public isAdministrator(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 1);
  }
  // Is Project Administrator
  public isProducer(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 2);
  }
  // Is Project Administrator
  public isDesigner(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 3);
  }
  // Is Project Administrator
  public isCollaborator(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 4);
  }
  // Is Project Administrator
  public isReader(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 5);
  }

  // ===================== MINIMUM ROLES =====================
  // =========================================================

  // Is Project Administrator
  public isAdministratorOrMore(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 0) ||
      this.hasUserRole(roles, 1);
  }
  // Is Project Administrator
  public isProducerOrMore(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 0) ||
      this.hasUserRole(roles, 1) ||
      this.hasUserRole(roles, 2);
  }
  // Is Project Administrator
  public isDesignerOrMore(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 0) ||
      this.hasUserRole(roles, 1) ||
      this.hasUserRole(roles, 2) ||
      this.hasUserRole(roles, 3);
  }
  // Is Project Administrator
  public isCollaboratorOrMore(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 0) ||
      this.hasUserRole(roles, 1) ||
      this.hasUserRole(roles, 2) ||
      this.hasUserRole(roles, 3) ||
      this.hasUserRole(roles, 4);
  }
  // Is Project Administrator
  public isReaderOrMore(roles: any[] = []): boolean {
    return this.hasUserRole(roles, 0) ||
      this.hasUserRole(roles, 1) ||
      this.hasUserRole(roles, 2) ||
      this.hasUserRole(roles, 3) ||
      this.hasUserRole(roles, 4) ||
      this.hasUserRole(roles, 5);
  }


  // =================== OWNER PERMISSIONS ===================
  // =========================================================

  // Can Add Project Completion And Welcome Message
  public canAddProjectCompletionAndWelcomeMessage(roles: any[] = []): boolean {
    const role = 0;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[0].id);
  }
  // Can Add Project Informed Consent
  public canAddProjectInformedConsent(roles: any[] = []): boolean {
    const role = 0;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[1].id);
  }
  // Create projects - Conditional CSI License
  public canCreateProjects(roles: any[] = []): boolean {
    const role = 0;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[2].id);
  }


  // =============== ADMINISTRATOR PERMISSIONS ===============
  // =========================================================

  // Can Deactivate Participants
  public canDeactivateParticipants(roles: any[] = []): boolean {
    const role = 1;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[0].id);
  }
  // Can Delete Analysis Acenarios
  public canDeleteAnalysisAcenarios(roles: any[] = []): boolean {
    const role = 1;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[1].id);
  }
  // Edit General Project Settings And All Project General Content
  public canEditGeneralProjectSettings(roles: any[] = []): boolean {
    const role = 1;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[2].id);
  }
  // Can Reset Participant Passwords
  public canResetParticipantPasswords(roles: any[] = []): boolean {
    const role = 1;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[3].id);
  }
  // Can Role Management
  public canRoleManagement(roles: any[] = []): boolean {
    const role = 1;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[4].id);
  }


  // ================= PRODUCER PERMISSIONS ==================
  // =========================================================

  // Can Add, Edit Analysis
  public canAddEditAnalysis(roles: any = []): boolean {
    const role = 2;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[0].id);
  }
  // Can Approve And Reject Participant’s Data
  public canApproveAndRejectParticipantData(roles: any = []): boolean {
    const role = 2;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[1].id);
  }
  // Can Create And Edit Reports
  public canAddEditReports(roles: any = []): boolean {
    const role = 2;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[2].id);
  }
  // Can Delete Activities
  public canDeleteActivities(roles: any = []): boolean {
    const role = 2;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[3].id);
  }
  // Can Edit Activity Instructions
  public canEditActivityInstructions(roles: any = []): boolean {
    const role = 2;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[4].id);
  }
  // Can Export Entire Project
  public canExportEntireProject(roles: any = []): boolean {
    const role = 2;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[5].id);
  }
  // Can Manage Activity Settings: Includes Identification And Open/Close Status
  public canManageActivitySettings(roles: any = []): boolean {
    const role = 2;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[6].id);
  }


  // ================= DESIGNER PERMISSIONS ==================
  // =========================================================

  // Can Add, Edit Statements, Rating Questions And Participant Questions
  public canAddEditStatementsRatingQuestionsAndParticipantQquestions(roles: any[] = []): boolean {
    const role = 3;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[0].id);
  }
  // Can Add Participants, Assign Activities
  public canAddParticipantsAssignActivities(roles: any[] = []): boolean {
    const role = 3;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[1].id);
  }
  // Can Add, Edit The Project Informed Consent And Focus Prompt
  public canAddEditTheProjectInformedConsentAndFocusPrompt(roles: any[] = []): boolean {
    const role = 3;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[2].id);
  }


  // =============== COLLABORATOR PERMISSIONS ================
  // =========================================================

  // Can Enter Data For Offline Collected Activities: Participant Questions, Sorting And Rating
  public CanEnterDataForOfflineCollectedActivities(roles: any[] = []): boolean {
    const role = 4;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[0].id);
  }


  // ================== READER PERMISSIONS ===================
  // =========================================================

  // Can View All Analysis & Report Screens
  public CanViewAllAnalysisReportScreens(roles: any[] = []): boolean {
    const role = 5;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[0].id);
  }
  // Can View Project Activities And Participants
  public CanViewProjectActivitiesAndParticipants(roles: any[] = []): boolean {
    const role = 5;
    return this.hasPermission(roles, role, ROLES_AND_PERMISSIONS[role].permissions[1].id);
  }

  // ================== SPECIAL PERMISSIONS ===================
  // ==========================================================
  // Can Delete Participant Project
  public canDeleteParticipantProject(permissions: any[] = []): boolean {
    const role = 6;
    return this.hasSpecialPermission(permissions, ROLES_AND_PERMISSIONS[role].permissions[0].id);
  }
  // Can Flip Scale
  public canFlipScale(permissions: any[] = []): boolean {
    const role = 6;
    return this.hasSpecialPermission(permissions, ROLES_AND_PERMISSIONS[role].permissions[1].id);
  }
  // Can Relocate Points
  public canRelocatePoints(permissions: any[] = []): boolean {
    const role = 6;
    return this.hasSpecialPermission(permissions, ROLES_AND_PERMISSIONS[role].permissions[2].id);
  }

  // ================== PROJECT PERMISSIONS ===================
  // ==========================================================

  // Is Read Only Project
  public isProjectReadonly() {
    return this.getProjectStatus() !== 1;
  }

  // can access to report
  public canProjectReport() {
    return (this.getProjectStatus() === 1 || this.getProjectStatus() === 5 || this.getProjectStatus() === 6);
  }

  public canProjectAnalisys() {
    return (this.getProjectStatus() === 1 || this.getProjectStatus() === 4 || this.getProjectStatus() === 6);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/roles-and-permissions-setup.service.ts